import { useMemo } from "react";
import DOMPurify from "dompurify";
import { isRichTextHtml } from "~/helpers/richText";
import { cn } from "~/lib/utils";

// Tailwind preflight strips list styling, so both the editor content area and
// the rendered output share these classes to keep the two looking identical.
export const richTextClasses =
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 break-words " +
  "[&_h1]:text-[1.5em] [&_h1]:font-bold [&_h2]:text-[1.25em] [&_h2]:font-semibold [&_h3]:text-[1.125em] [&_h3]:font-semibold " +
  "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4";

// Rich text is the only HTML with links the user authored, so it gets its own
// DOMPurify instance: the hook below would otherwise also rewrite the anchors
// in the document HTML the other call sites render.
const purify = DOMPurify(window);

// Links leave a page that often holds an unsaved form, so they open in a new
// tab. `target` is not in DOMPurify's allowlist, but attributes set from this
// hook are kept, and `rel` closes the tabnabbing hole that opens.
purify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "A") return;
  node.setAttribute("target", "_blank");
  node.setAttribute("rel", "noreferrer");
});

interface Props {
  value: string;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

export function RichTextView({ value, className, ref }: Props) {
  const sanitizedHtml = useMemo(
    () => (isRichTextHtml(value) ? purify.sanitize(value) : null),
    [value],
  );

  if (sanitizedHtml === null) {
    return (
      <div ref={ref} className={cn("whitespace-pre-wrap", className)}>
        {value}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(richTextClasses, className)}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}
